import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../context/AuthContext";
import { Search, Mail, Globe, MapPin, ArrowRight } from "lucide-react";
import { NETWORK_ROLES } from "../constants/roles";

const ROLES = ["All", ...NETWORK_ROLES];

function ProCard({ p }) {
  const initials = p.name.split(" ").map((n) => n[0]).slice(0, 2).join("");
  return (
    <article className="bg-white border-2 border-[#0A0A0A] p-6 flex flex-col gap-4 hover:border-[#1A5DFF] transition-colors" data-testid={`pro-card-${p.id}`}>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-[#0A0A0A] text-[#FF2E63] flex items-center justify-center font-heading font-black text-lg">{initials}</div>
        <div>
          <h3 className="font-heading font-bold text-lg text-[#0A0A0A] leading-tight">{p.name} {p.pronouns && <span className="text-xs font-normal text-[#1A5DFF]">({p.pronouns})</span>}</h3>
          <div className="text-xs uppercase tracking-[0.18em] text-[#FF2E63] font-bold mt-1">{p.role}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-sm text-[#2C2C2C]">
        <MapPin className="w-4 h-4" /> {p.location}
      </div>
      <p className="text-sm text-[#2C2C2C] leading-relaxed">{p.bio}</p>
      {p.neurodivergence?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {p.neurodivergence.map((n) => (
            <span key={n} className="text-xs uppercase tracking-[0.14em] px-2 py-1 bg-[#FFE8A8] text-[#0A0A0A] border-2 border-[#0A0A0A]">{n}</span>
          ))}
        </div>
      )}
      {p.skills?.length > 0 && (
        <div className="text-xs text-[#2C2C2C]"><span className="font-bold text-[#0A0A0A] uppercase tracking-[0.16em]">Skills · </span>{p.skills.join(" · ")}</div>
      )}
      <div className="flex items-center gap-4 pt-2 border-t border-[#0A0A0A] mt-auto">
        {p.email && (
          <a href={`mailto:${p.email}`} className="text-sm text-[#0A0A0A] hover:text-[#FF2E63] inline-flex items-center gap-1.5" data-testid={`pro-email-${p.id}`}>
            <Mail className="w-4 h-4" /> Email
          </a>
        )}
        {p.website && (
          <a href={p.website} target="_blank" rel="noopener noreferrer" className="text-sm text-[#0A0A0A] hover:text-[#FF2E63] inline-flex items-center gap-1.5" data-testid={`pro-website-${p.id}`}>
            <Globe className="w-4 h-4" /> Website
          </a>
        )}
      </div>
    </article>
  );
}

export default function Network() {
  const { user } = useAuth();
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/network/professionals`, { withCredentials: true });
        setPros(r.data || []);
      } catch (e) {
        setError("Could not load the directory.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = pros;
    if (role !== "All") list = list.filter((p) => p.role === role);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((p) =>
        [p.name, p.role, p.location, p.bio, ...(p.skills || []), ...(p.neurodivergence || [])]
          .join(" ").toLowerCase().includes(q)
      );
    }
    return list;
  }, [pros, role, query]);

  return (
    <div className="bg-[#FFF8E7]" data-testid="page-network">
      {/* HERO — matches Landing pop-art style */}
      <section className="relative overflow-hidden border-b-2 border-[#0A0A0A]" data-testid="network-hero">
        <div className="absolute inset-0 halftone-blue opacity-[0.07]" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="inline-block bg-[#FF2E63] text-[#FFF8E7] px-3 py-1 text-xs uppercase tracking-[0.2em] font-bold mb-4 border-2 border-[#0A0A0A]">Members · Network directory</div>
          <h1 className="font-heading font-black text-[#0A0A0A] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[0.95]">
            The <span className="text-[#1A5DFF] [text-shadow:3px_3px_0_#0A0A0A]">directory.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#0A0A0A] leading-relaxed">
            Vetted neurodivergent professionals open to work — editors, producers, actors, lighting and sound, DPs, composers and writers.
            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </p>
          <div className="mt-8">
            <Link
              to="/network/join"
              data-testid="network-join-cta"
              className="bg-[#FFD93D] text-[#0A0A0A] hover:bg-[#FFCB05] border-2 border-[#0A0A0A] pop-shadow rounded-none px-6 py-3 text-sm font-medium inline-flex items-center gap-2"
            >
              Join the directory <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 mb-8" data-testid="network-controls">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#1A5DFF]" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, skill, location…"
                aria-label="Search the directory"
                data-testid="network-search"
                className="w-full bg-white border-2 border-[#0A0A0A] text-[#0A0A0A] pl-11 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#FF2E63] rounded-none"
              />
            </div>
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter by role" data-testid="role-filters">
              {ROLES.map((r) => (
                <button
                  key={r}
                  role="tab"
                  aria-selected={r === role}
                  data-testid={`role-${r.toLowerCase()}`}
                  onClick={() => setRole(r)}
                  className={`px-4 py-2 text-sm font-bold uppercase tracking-[0.12em] border-2 border-[#0A0A0A] transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FF2E63] ${
                    r === role
                      ? "bg-[#FF2E63] text-[#FFF8E7] pop-shadow"
                      : "bg-[#FFF8E7] text-[#0A0A0A] hover:bg-[#FFD93D]"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="text-sm text-[#2C2C2C] mb-6" data-testid="network-count">{filtered.length} {filtered.length === 1 ? "professional" : "professionals"} found</div>

          {loading && <div className="text-[#2C2C2C]" data-testid="network-loading">Loading directory…</div>}
          {error && <div className="text-[#FF2E63]" data-testid="network-error">{error}</div>}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="pros-grid">
            {filtered.map((p) => <ProCard key={p.id} p={p} />)}
          </div>

          {!loading && filtered.length === 0 && (
            <div className="text-[#2C2C2C]" data-testid="network-empty">No matches. Try clearing filters.</div>
          )}
        </div>
      </section>
    </div>
  );
}
